import { ALL_TAB_KEYS, isTabKey, type TabKey } from "../../lib/tab-registry";
import { validateUsername } from "../../lib/username-validation";
import type { NewAdmin } from "./iam-api";

/**
 * Bulk-admin CSV: name,email,password,username,dashboard_tabs
 * No account_type column on purpose — every row becomes an admin.
 * dashboard_tabs is pipe-separated tab ids from the registry.
 */
export const ADMIN_CSV_HEADERS = ["name", "email", "password", "username", "dashboard_tabs"] as const;

export type AdminCsvError = { row: number; message: string };

const parseCsvRows = (content: string): string[][] => {
  const normalized = content.replace(/^\uFEFF/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i]!;
    if (char === '"') {
      if (inQuotes && normalized[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      row.push(field);
      field = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      if (char === "\r" && normalized[i + 1] === "\n") i += 1;
      continue;
    }
    field += char;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

export const parseTabList = (raw: string): { tabs: TabKey[]; unknown: string[] } => {
  const tokens = raw
    .split("|")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const tabs: TabKey[] = [];
  const unknown: string[] = [];
  for (const token of tokens) {
    if (isTabKey(token)) {
      if (!tabs.includes(token)) tabs.push(token);
    } else {
      unknown.push(token);
    }
  }
  return { tabs: ALL_TAB_KEYS.filter((tab) => tabs.includes(tab)), unknown };
};

export const parseAdminCsv = (content: string): { users: NewAdmin[]; errors: AdminCsvError[] } => {
  const rows = parseCsvRows(content);
  if (rows.length === 0) return { users: [], errors: [{ row: 0, message: "CSV file is empty." }] };
  const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  const headerOk =
    header.length === ADMIN_CSV_HEADERS.length &&
    ADMIN_CSV_HEADERS.every((h, i) => header[i] === h);
  if (!headerOk) {
    return {
      users: [],
      errors: [{ row: 1, message: `Headers must be exactly: ${ADMIN_CSV_HEADERS.join(", ")}` }],
    };
  }

  const users: NewAdmin[] = [];
  const errors: AdminCsvError[] = [];
  const seenEmails = new Set<string>();
  for (let index = 1; index < rows.length; index += 1) {
    const row = rows[index] ?? [];
    if (row.every((cell) => cell.trim().length === 0)) continue;
    const rowNumber = index + 1;
    if (row.length !== ADMIN_CSV_HEADERS.length) {
      errors.push({ row: rowNumber, message: `Must have exactly ${ADMIN_CSV_HEADERS.length} columns.` });
      continue;
    }
    const [nameRaw, emailRaw, password, usernameRaw, tabsRaw] = row as [string, string, string, string, string];
    const email = emailRaw.trim().toLowerCase();
    const name = nameRaw.trim() || email.split("@")[0] || "";
    const username = usernameRaw.trim();
    if (!email.includes("@")) errors.push({ row: rowNumber, message: "email is invalid." });
    if (password.length < 8) errors.push({ row: rowNumber, message: "password must be at least 8 characters." });
    if (username) {
      const problem = validateUsername(username);
      if (problem) errors.push({ row: rowNumber, message: `username: ${problem}` });
    }
    const { tabs, unknown } = parseTabList(tabsRaw);
    if (unknown.length) errors.push({ row: rowNumber, message: `Unknown dashboard_tabs: ${unknown.join(", ")}` });
    if (seenEmails.has(email)) errors.push({ row: rowNumber, message: "Duplicate email in file." });
    seenEmails.add(email);
    if (errors.some((e) => e.row === rowNumber)) continue;
    users.push({ name, email, password, username: username || undefined, permissions: tabs });
  }
  if (users.length === 0 && errors.length === 0) errors.push({ row: 0, message: "No admin rows found." });
  return { users, errors };
};

export const getAdminCsvTemplate = (): string =>
  [
    ADMIN_CSV_HEADERS.join(","),
    "Content Person,content@evertutor.ai,ChangeMe123!,content.person,questionbank",
    "Sales Person,sales@evertutor.ai,ChangeMe123!,sales.person,usage|conversations",
  ].join("\n");
